import React from 'react';
import {reportUsersAllowed, SubUser} from "../../../services/models/sub_user";
import {UserSelect} from "../UserSelect/UserSelect";
import {DropdownIndicator} from "../../shared/DropdownIndicator";
import Select, {components, GroupBase, SingleValue, ActionMeta, StylesConfig} from "react-select";
import {customStyles, SelectOption} from "../../shared/selectStyles";
import {reportFieldLabels} from '../../../types/report.consts';
import styles from "./LogsChart.module.css"
import {FormActionButton} from "../../shared/buttons/FormActionButton";
import {useFetchReportsChartData} from '../../../hooks/useQuery/useFetchReportsChartData';
import {Chart} from "../Chart/Chart";
import {getWeekDateRange} from "../../../Utils/translateDate";
import {useFetchReportsPlanChartData} from "../../../hooks/useQuery/useFetchReportsPlanChartData";

// Расширяем интерфейс Date для добавления метода getWeek
declare global {
    interface Date {
        getWeek(): number;
    }
}

// Реализация метода getWeek для Date
Date.prototype.getWeek = function(): number {
    const date = new Date(this.getTime());
    date.setHours(0, 0, 0, 0);
    // Устанавливаем дату на четверг текущей недели
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    // Первый четверг года
    const firstThursday = new Date(date.getFullYear(), 0, 4);
    // Устанавливаем первый четверг года на четверг первой недели
    firstThursday.setDate(firstThursday.getDate() + 3 - (firstThursday.getDay() + 6) % 7);
    // Проверяем, не находится ли дата в предыдущем году
    if (date.getTime() < firstThursday.getTime()) {
        return 0; // Или 53, в зависимости от вашей логики
    }
    // Возвращаем номер недели
    return Math.floor(((date.getTime() - firstThursday.getTime()) / 86400000) / 7) + 1;
};

interface LogsTableProps {
    users?: SubUser[];
    onUserChange?: (userId: string) => void;
    annualInspectionPlan?:number;
    currentUser?: SubUser | null;
}

// Расширяем тип SelectOption для наших опций
interface ChartSelectOption extends SelectOption {
    section: string;
}

const groupedOptions: GroupBase<ChartSelectOption>[] = [
    {
        label: 'Результаты ПО',
        options: Object.entries(reportFieldLabels.resultPO)
            .map(([k, v]) => ({value: k, label: v as string, fullLabel: v as string, section: 'resultPO'}))
    },
    {
        label: 'Результаты ПО (дети)',
        options: Object.entries(reportFieldLabels.resultPOChildren)
            .map(([k, v]) => ({value: k, label: v as string, fullLabel: v as string, section: 'resultPOChildren'}))
    }
];

export const LogsChart: React.FC<LogsTableProps> = ({users, currentUser}) => {
    // Инициализируем выбранный параметр при первой загрузке компонента
    const [selectedParam, setSelectedParam] = React.useState<string | null>("examinedTotal");
    const [selectedUserForSelect, setSelectedUserForSelect] = React.useState<string | null>(null);
    const [queryParams, setQueryParams] = React.useState<{
        owner: string | null,
        section: string | null,
        param: string | null
    }>({owner: null, section: "resultPO", param: "examinedTotal"});

    const isAllowedUser = currentUser && reportUsersAllowed.includes(currentUser.email);

    React.useEffect(() => {
        if (users && users.length > 0 && !selectedUserForSelect) {
            if (isAllowedUser && currentUser) {
                const userId = currentUser._id || currentUser.id;
                if (userId) {
                    setSelectedUserForSelect(userId);
                }
            } else {
                const firstUser = users[0];
                const userId = firstUser._id || firstUser.id;
                if (userId) {
                    setSelectedUserForSelect(userId);
                }
            }
        }
    }, [users, selectedUserForSelect, isAllowedUser, currentUser]);

    // Находим опцию, соответствующую выбранному параметру
    const selectedOption = React.useMemo(() => {
        return groupedOptions
            .flatMap(group => group.options)
            .find(opt => opt.value === selectedParam) || null;
    }, [selectedParam]);

    // Синхронизируем queryParams с выбранными параметрами (автоматически)
    React.useEffect(() => {
        // Находим текущий section по выбранному параметру
        const selectedOpt = groupedOptions
            .flatMap(group => group.options)
            .find(opt => opt.value === selectedParam);
        const section = selectedOpt ? selectedOpt.section : null;
        setQueryParams(prev => {
            // Обновляем только если что-то поменялось, чтобы не было лишних запросов
            if (
                prev.param !== selectedParam ||
                prev.section !== section ||
                prev.owner !== selectedUserForSelect
            ) {
                return {
                    owner: selectedUserForSelect,
                    section,
                    param: selectedParam
                };
            }
            return prev;
        });
    }, [selectedParam, selectedUserForSelect]);

    // Получаем данные для графика
    const {reportsChartData, reportsChartError, reportsChartIsLoading} = useFetchReportsChartData(
        queryParams.owner || '',
        queryParams.section || '',
        queryParams.param || ''
    );
    const {reportsPlanChartData, reportsPlanChartError, reportsPlanChartIsLoading} = useFetchReportsPlanChartData(
        queryParams.owner || '',
        queryParams.section || '',
        queryParams.param || '',
        Boolean(queryParams.param === "examinedTotal")
    );

    // Получаем данные из API
    const chartDataFromAPI = React.useMemo(() => {
        if (!Array.isArray(reportsChartData) || reportsChartData.length === 0) {
            return new Map<number, any>();
        }
        
        // Создаем карту недель и их данных
        const weekDataMap = new Map<number, any>();
        reportsChartData.forEach((item: any) => {
            weekDataMap.set(item.week, item);
        });
        
        return weekDataMap;
    }, [reportsChartData]);

    // Находим последнюю неделю, для которой есть запись в данных (независимо от значения)
    const lastWeekWithData = React.useMemo(() => {
        if (!Array.isArray(reportsChartData) || reportsChartData.length === 0) {
            return 0;
        }
        
        // Находим максимальную неделю, для которой есть запись в данных
        let maxWeek = 0;
        reportsChartData.forEach((item: any) => {
            if (item.week > maxWeek) {
                maxWeek = item.week;
            }
        });
        
        return maxWeek;
    }, [reportsChartData]);

    // Создаем массив недель до последней недели с данными
    const allWeeks = React.useMemo(() => {
        if (lastWeekWithData === 0) {
            // Если нет данных, показываем текущую неделю
            const currentWeek = new Date().getWeek();
            return Array.from({ length: currentWeek }, (_, i) => i + 1);
        }
        // Показываем недели до последней недели с данными
        return Array.from({ length: lastWeekWithData }, (_, i) => i + 1);
    }, [lastWeekWithData]);

    // Подготавливаем метки только для недель до последней с данными
    const chartLabels = React.useMemo(() => {
        const currentYear = new Date().getFullYear();
        return allWeeks.map(week => getWeekDateRange(week, currentYear));
    }, [allWeeks]);

    // Подготавливаем данные для главного графика (нарастающим итогом)
    const chartData = React.useMemo(() => {
        if (!Array.isArray(reportsChartData) || reportsChartData.length === 0) {
            return allWeeks.map(() => 0);
        }

        let lastKnownValue = 0;
        
        return allWeeks.map(week => {
            const item = chartDataFromAPI.get(week);
            
            if (item) {
                // Если есть данные для этой недели, используем их
                lastKnownValue = queryParams.param === "examinedTotal" ? (item.sumToWeek ?? 0) : (item.value ?? 0);
                return lastKnownValue;
            } else {
                // Если данных нет, используем последнее известное значение
                return lastKnownValue;
            }
        });
    }, [reportsChartData, allWeeks, chartDataFromAPI, queryParams.param]);

    // Данные для столбчатого графика (только еженедельные значения)
    const chartBarData = React.useMemo(() => {
        if (!Array.isArray(reportsChartData) || reportsChartData.length === 0) {
            return [];
        }

        // Для столбчатого графика используем только фактические данные из API
        return reportsChartData.map((item: any) => item.value ?? 0);
    }, [reportsChartData]);

    // Подготавливаем тултипы только для недель с данными
    const chartTooltips = React.useMemo(() => {
        const currentYear = new Date().getFullYear();
        
        return allWeeks.map(week => {
            const item = chartDataFromAPI.get(week);
            
            if (item) {
                // Если есть данные для этой недели
                return item.sumToWeekWithPrevYear ? [
                    `Период: ${getWeekDateRange(week, currentYear)}`,
                    `Значение: ${item.value ?? 0} \n\n`,
                    `Нарастающим итогом, с начала года: ${item.sumToWeek ?? 0} `,
                    `Суммарно с данными ${currentYear - 1} года: ${item.sumToWeekWithPrevYear ?? 0}`
                ] : [
                    `Период: ${getWeekDateRange(week, currentYear)}`,
                    `Значение: ${item.value ?? 0}`,
                    `Нарастающим итогом, с начала года: ${item.sumToWeek ?? 0}`
                ];
            } else {
                // Если данных нет
                return [
                    `Период: ${getWeekDateRange(week, currentYear)}`,
                    `Значение: нет данных`,
                    `Нарастающим итогом, с начала года: ${chartData[week - 1] ?? 0}`
                ];
            }
        });
    }, [reportsChartData, allWeeks, chartDataFromAPI, chartData]
    );

    const section = queryParams.section as keyof typeof reportFieldLabels | undefined;
    const param = queryParams.param as string | undefined;
    const chartTitle =
        section && param && reportFieldLabels[section] && (reportFieldLabels[section] as Record<string, string>)[param]
            ? `Показатели параметра:  ${(reportFieldLabels[section] as Record<string, string>)[param]}`
            : 'Показатели параметра';
            
    // Метки и тултипы для столбчатого графика (только фактические данные)
    const barChartLabels = React.useMemo(() => {
        if (!Array.isArray(reportsChartData) || reportsChartData.length === 0) {
            return [];
        }
        return reportsChartData.map((item: any) => getWeekDateRange(item.week, new Date().getFullYear()));
    }, [reportsChartData]);

    const barChartTooltips = React.useMemo(() => {
        if (!Array.isArray(reportsChartData) || reportsChartData.length === 0) {
            return [];
        }
        return reportsChartData.map((item: any) => [
            `Период: ${getWeekDateRange(item.week, new Date().getFullYear())}`,
            `Значение: ${item.value ?? 0}`
        ]);
    }, [reportsChartData]);

    // Интерфейс для данных планового графика
    interface PlanChartDataItem {
        week: number;
        value: number;
        sumValue: number; // Добавляем поле для накопленной суммы
    }
    
    // Преобразуем данные планового графика в формат, подходящий для компонента Chart
    const planChartData = React.useMemo(() => {
        if (!reportsPlanChartData || !Array.isArray(reportsPlanChartData)) return [];
        
        // Создаем массив с данными плана только до последней недели с данными
        const planData: number[] = [];
        
        // Создаем маппинг недель и значений (используем sumValue вместо value)
        const planDataMap = new Map<number, number>();
        (reportsPlanChartData as PlanChartDataItem[]).forEach((item: PlanChartDataItem) => {
            planDataMap.set(item.week, item.sumValue); // Используем sumValue вместо value
        });
        
        // Заполняем массив данных только до последней недели с данными
        allWeeks.forEach((week: number) => {
            planData.push(planDataMap.get(week) || 0);
        });
        
        return planData;
    }, [reportsPlanChartData, allWeeks]);
    
    // Создаем тултипы для планового графика
    const planChartTooltips = React.useMemo(() => {
        if (!reportsPlanChartData || !Array.isArray(reportsPlanChartData)) return [];
        
        const tooltips: string[][] = [];
        
        // Создаем маппинги для еженедельных и накопленных значений
        const weeklyValueMap = new Map<number, number>();
        const sumValueMap = new Map<number, number>();
        
        (reportsPlanChartData as PlanChartDataItem[]).forEach((item: PlanChartDataItem) => {
            weeklyValueMap.set(item.week, item.value);
            sumValueMap.set(item.week, item.sumValue);
        });
        
        // Получаем текущий год для расчета дат недель
        const currentYear = new Date().getFullYear();
        
        allWeeks.forEach((week: number) => {
            const weeklyValue = weeklyValueMap.get(week) || 0;
            const sumValue = sumValueMap.get(week) || 0;
            const dateRange = getWeekDateRange(week, currentYear);
            
            tooltips.push([
                `План нарастающим итогом: ${sumValue}`, 
                `План на неделю: ${weeklyValue}`, 
                `Неделя ${week}: ${dateRange}`
            ]);
        });
        
        return tooltips;
    }, [reportsPlanChartData, allWeeks]);

    const MemoChart = React.useMemo(() => {
        if (!chartLabels.length || !chartData.length) return null;

        // Формируем заголовок графика
        const chartTitle = selectedOption ? selectedOption.fullLabel : '';

        if (queryParams.section && queryParams.param) {
            // Для основного графика нарастающим итогом используем все недели года
            if (queryParams.param === "examinedTotal") {
                return (
                    <div>
                        <Chart
                            labels={chartLabels}
                            data={chartData.map(val => typeof val === 'number' ? val : 0)}
                            tooltips={chartTooltips}
                            title={chartTitle}
                            type="line"
                            secondaryData={planChartData}
                            secondaryLabel="Плановые значения"
                            secondaryTooltips={planChartTooltips}
                        />
                        <Chart
                            labels={barChartLabels}
                            data={chartBarData.map(val => typeof val === 'number' ? val : 0)}
                            tooltips={barChartTooltips}
                            title={`Еженедельные данные: ${chartTitle}`}
                            type="bar"
                        />
                    </div>
                );
            } else {
                return (
                    <Chart
                        labels={barChartLabels}
                        data={chartBarData.map(val => typeof val === 'number' ? val : 0)}
                        tooltips={barChartTooltips}
                        title={chartTitle}
                    />
                );
            }
        }
        return null;
    }, [chartLabels, chartData, chartBarData, chartTooltips, planChartData, planChartTooltips, queryParams.param, queryParams.section]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.filterWrapper}>

                <div className={styles.userSelectWrapper}>
                    <UserSelect
                        users={users}
                        selected={selectedUserForSelect}
                        onUserChange={setSelectedUserForSelect}
                        disabled={isAllowedUser}
                    />
                </div>
                <div className={styles.paramSelectWrapper}>
                    <Select
                        isMulti={false}
                        classNamePrefix="select"
                        name="param"
                        options={groupedOptions}
                        isSearchable={false}
                        isClearable={false}
                        placeholder={"Выберите параметр"}
                        noOptionsMessage={() => "Нет доступных опций"}
                        components={{
                            IndicatorSeparator: () => null,
                            DropdownIndicator,
                            Option: components.Option
                        }}
                        hideSelectedOptions={false}
                        closeMenuOnSelect={true}
                        blurInputOnSelect={true}
                        value={selectedOption as SingleValue<SelectOption>}
                        onChange={(newValue: SingleValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) => {
                            if (newValue) {
                                const option = newValue as ChartSelectOption;
                                setSelectedParam(option.value);
                            } else {
                                setSelectedParam(null);
                            }
                        }}
                        styles={customStyles}
                    />
                </div>
               {/* <div className={styles.applySelectWrapper}>
                    <FormActionButton
                        text={"Применить"}
                        onClick={() => {
                            const selectedOption = groupedOptions
                                .flatMap(group => group.options)
                                .find(opt => opt.value === selectedParam);
                            const section = selectedOption ? selectedOption.section : null;
                            const param = selectedParam;
                            const owner = selectedUserForSelect;
                            setQueryParams({owner, section, param});
                        }}
                        disabled={!selectedUserForSelect || !selectedParam}
                    />
                </div>*/}
            </div>
            <div>
                {reportsChartIsLoading ? (
                    <div style={{textAlign: 'center', color: '#888', marginTop: 40}}>
                        Загрузка данных...
                    </div>
                ) : reportsChartError ? (
                    <div style={{textAlign: 'center', color: 'red', marginTop: 40}}>
                        Ошибка загрузки графика
                    </div>
                ) : MemoChart || (
                    <div style={{textAlign: 'center', color: '#888', marginTop: 40}}>
                        График не отображается. Выберите параметры.
                    </div>
                )}
            </div>
        </div>
    )
};